import firebase from '@/firebase'
const db = firebase.db
const functions = firebase.functions

const state = {
  simulations: [],
  simulationsLoading: true, // true: loading simulations, false: simulations loaded
  subscribedClient: null, // client id whose simulations onSnapshot listener is currently active
  unsubscribeListener: function () {},
  userSimulations: [],
  userSimulationsLoading: false,
  userSimulationsHasMore: false,
  userSimulationsLastVisible: null,
  accessibleRecentSimulations: [],
  accessibleRecentSimulationsLoading: false,
  accessibleRecentSimulationsHasMore: false,
  accessibleRecentSimulationsLastVisible: null
}

const getters = {
  simulations: state => {
    return state.simulations
  },
  simulationsLoading: state => {
    return state.simulationsLoading
  },
  // True when the (hidden) What-If Scenario "Strategy Check" plans exist for the current
  // model — i.e. they were already computed or are currently being computed. Old models that
  // never had these plans generated return false, so the section can be hidden for them.
  hasStrategyCheckPlans: state => {
    return state.simulations.some(simulation => simulation.strategy_check)
  },
  // Budget Simulator docs only; their grid points are ordinary plans that point back to them.
  budgetSimulations: state => {
    return state.simulations.filter(simulation => simulation.type === 'budget_simulation')
  },
  budgetSimulationPoints: state => parentId => {
    return state.simulations.filter(simulation => simulation.budget_simulation_id === parentId)
  },
  userSimulations: state => {
    return state.userSimulations
  },
  userSimulationsLoading: state => {
    return state.userSimulationsLoading
  },
  accessibleRecentSimulations: state => {
    return state.accessibleRecentSimulations
  },
  accessibleRecentSimulationsLoading: state => {
    return state.accessibleRecentSimulationsLoading
  }
}

const actions = {
  loadSimulations (context, payload) {
    if (payload.client) {
      // Already subscribed to this client's simulations: the onSnapshot listener keeps
      // state live, so re-loading would only clear + re-fetch needlessly. Several views
      // (App.vue, planSimulator, planOptimizer, WhatIfScenario) each call this on their
      // own currentClient watcher; without this guard every navigation would empty the
      // simulations array for a moment, flickering anything gated on it (e.g. the
      // What-If Scenario menu item via hasStrategyCheckPlans).
      if (context.state.subscribedClient === payload.client) {
        return
      }
      // Always unsubscribe active simulation onSnapshot listener before setting a new one (avoids adding previously active client's simulations to current view)
      context.state.unsubscribeListener()
      // Always clear simulations before loading simulations
      context.commit('clearSimulations')
      context.commit('setSubscribedClient', payload.client)
      const unsubscribeListener = db.collection('clients').doc(payload.client).collection('simulations').orderBy('date_created', 'desc').onSnapshot(function (snapshot) {
        // Only consider server snapshots, ignore local snapshots
        // TODO: Maybe consider local snapshots sometimes (adding, deleting and editing locally?)
        if (!snapshot.metadata.hasPendingWrites) {
          /* console.log('Received server snapshot')
          console.log(snapshot) */
          var added = []
          var modified = []
          var removed = []
          snapshot.docChanges().forEach(function (change) {
            /* console.log('Change:')
            console.log(change) */
            const document = {
              id: change.doc.id,
              ...change.doc.data()
            }
            try {
              // Convert from Firestore timestamp to Date
              document.date_created = document.date_created.toDate()
            } catch (error) {}
            if (change.type === 'added') {
              added.push(document)
            }
            if (change.type === 'modified') {
              modified.push(document)
            }
            if (change.type === 'removed') {
              removed.push(document)
            }
          })
          context.commit('setSimulations', {
            added: added,
            modified: modified,
            removed: removed
          })
        } else {
          console.log('Local snapshot')
          console.log(snapshot)
        }
      })
      context.commit('setUnsubscribeListener', {
        unsubscribeListener: unsubscribeListener
      })
    }
  },
  // Triggers on-demand creation of the hidden What-If Scenario (Strategy Check) plans for an
  // Ad-Hoc model, without recalculating the Model Report. The Cloud Function rejects unless the
  // model has no strategy-check plans already created or processing.
  triggerStrategyCheckPlans (context, payload) {
    return functions.httpsCallable('triggerStrategyCheckPlans')({
      clientId: payload.client,
      userId: payload.userId
    })
  },
  addSimulation (context, payload) {
    return new Promise((resolve, reject) => {
      db.collection('clients').doc(payload.client).collection('simulations').add({
        ...payload.simulation,
        date_created: firebase.firestore.FieldValue.serverTimestamp(), // Used for controlling cloud functions execution in updateSimulation
        created_by: payload.user,
        created_by_id: payload.userId
      }).then(function (docRef) {
        console.log('Simulation document written with ID: ', docRef.id)
        resolve()
      }).catch(function (error) {
        console.error('Error adding simulation document: ', error)
        reject(error)
      })
    })
  },
  // Writes a Budget Simulator and one plan per grid point at once, so the Budget Simulator never
  // exists without its points. Each point starts computing on its own through runSimulation.
  addBudgetSimulation (context, payload) {
    const simulationsRef = db.collection('clients').doc(payload.client).collection('simulations')
    const parentRef = simulationsRef.doc()
    const ownership = {
      date_created: firebase.firestore.FieldValue.serverTimestamp(),
      created_by: payload.user,
      created_by_id: payload.userId
    }
    const batch = db.batch()
    batch.set(parentRef, { ...payload.budgetSimulation, ...ownership })
    payload.points.forEach(point => {
      batch.set(simulationsRef.doc(), { ...point, budget_simulation_id: parentRef.id, ...ownership })
    })
    return batch.commit().then(() => parentRef.id)
  },
  // Rewrites a Budget Simulator with new settings and replaces its points: the old ones are deleted
  // (their onDelete closes their queue items) and the new ones start computing, all in one batch.
  editBudgetSimulation (context, payload) {
    const simulationsRef = db.collection('clients').doc(payload.client).collection('simulations')
    const parentRef = simulationsRef.doc(payload.id)
    const ownership = {
      date_created: firebase.firestore.FieldValue.serverTimestamp(),
      created_by: payload.user,
      created_by_id: payload.userId
    }
    const batch = db.batch()
    batch.set(parentRef, { ...payload.budgetSimulation, ...ownership })
    context.getters.budgetSimulationPoints(payload.id).forEach(point => {
      batch.delete(simulationsRef.doc(point.id))
    })
    payload.points.forEach(point => {
      batch.set(simulationsRef.doc(), { ...point, budget_simulation_id: parentRef.id, ...ownership })
    })
    return batch.commit().then(() => parentRef.id)
  },
  deleteSimulation (context, payload) {
    db.collection('clients').doc(payload.client).collection('simulations').doc(payload.simulation).delete().then(function () {
      console.log('Document successfully deleted!')
      db.collection('queue').where('simulationId', '==', payload.simulation).get().then((snapshot) => {
        if (!snapshot.empty) {
          const id = snapshot.docs[0].id
          db.collection('queue').doc(id).update({
            date_completed: new Date().toUTCString(),
            deleted_simulation: true
          }).then(function () {
            console.log('Queue item successfully tagged as deleted!')
          }).catch(function (error) {
            console.error('Error tagging queue item as deleted: ', error)
          })
        } else {
          console.log('Error. No queue item found')
        }
      })
    }).catch(function (error) {
      console.error('Error removing document: ', error)
    })
  },
  async editSimulation (context, payload) {
    db.collection('clients').doc(payload.client).collection('simulations').doc(payload.baseSimulation).set({
      ...payload.simulation,
      date_created: firebase.firestore.FieldValue.serverTimestamp(), // Used for controlling cloud functions execution in updateSimulation
      created_by: payload.user,
      created_by_id: payload.userId
    }).then(function (docRef) {
      console.log('Simulation document updated')
    }).catch(function (error) {
      console.error('Error updating simulation document: ', error)
    })
  },
  // Read by a Cloud Function, which falls back to the copy simulate() left in the queue once the
  // plan itself is gone, so a process reported to Slack can still be inspected after the user
  // deletes the plan. Resolves { data, source ('simulation' or 'queue'), queueMetadata }.
  exportSimulationData (context, payload) {
    return functions.httpsCallable('supportExport-getExportData')({
      type: 'plan',
      clientId: payload.clientId,
      simulationId: payload.simulationId
    }).then(result => result.data)
  },
  loadUserSimulations (context, params = {}) {
    context.commit('setUserSimulationsLoading', true)
    const getUserRecentPlans = functions.httpsCallable('userMetrics-getUserRecentPlans')
    const requestParams = {
      limit: params.limit || 20,
      startAfter: params.startAfter || null
    }
    getUserRecentPlans(requestParams)
      .then((result) => {
        const plans = result.data.items.map(plan => ({
          ...plan,
          // Convert ISO date string back to Date object
          date_created: plan.date_created ? new Date(plan.date_created) : null
        }))
        const payload = {
          simulations: plans,
          hasMore: result.data.hasMore,
          lastVisible: result.data.lastVisible
        }
        if (params.append) {
          context.commit('appendUserSimulations', payload)
        } else {
          context.commit('setUserSimulations', payload)
        }
      })
      .catch((error) => {
        console.error('Error loading user simulations:', error)
        context.commit('setUserSimulationsLoading', false)
      })
  },
  updateSimulationSettings (context, payload) {
    return db.collection('clients').doc(payload.client).collection('simulations')
      .doc(payload.simulation).update(payload.settings)
  },
  loadAccessibleRecentSimulations (context, params = {}) {
    context.commit('setAccessibleRecentSimulationsLoading', true)
    const getAccessiblePlans = functions.httpsCallable('userMetrics-getUserAccessibleRecentPlans')
    const requestParams = {
      limit: params.limit || 20,
      startAfter: params.startAfter || null
    }
    getAccessiblePlans(requestParams)
      .then((result) => {
        const simulations = result.data.items.map(plan => ({
          ...plan,
          date_created: plan.date_created ? new Date(plan.date_created) : null
        }))
        const payload = {
          simulations: simulations,
          hasMore: result.data.hasMore,
          lastVisible: result.data.lastVisible
        }
        if (params.append) {
          context.commit('appendAccessibleRecentSimulations', payload)
        } else {
          context.commit('setAccessibleRecentSimulations', payload)
        }
      })
      .catch((error) => {
        console.error('Error loading accessible recent simulations:', error)
        context.commit('setAccessibleRecentSimulationsLoading', false)
      })
  }
}

const mutations = {
  clearSimulations (state) {
    state.simulations = []
    state.simulationsLoading = true
  },
  setSimulations (state, payload) {
    // Only the first time simulations are loaded
    if (state.simulations.length === 0) {
      payload.added.map(addedSimulation => {
        state.simulations.push(addedSimulation)
      })
    }
    payload.modified.map(modifiedSimulation => {
      // If this is the first, just created, simulation
      if (state.simulations.length === 0) {
        console.log(`New simulation ${modifiedSimulation.id} detected and pushed`)
        state.simulations.push(modifiedSimulation)
      } else {
        let processed = false // Only process a modification once -> processed = true
        state.simulations.forEach((oldSimulation, index) => {
          // If this is an existing simulation, update it
          if (oldSimulation.id === modifiedSimulation.id) {
            console.log(`Simulation ${oldSimulation.id} has been updated`)
            state.simulations.splice(index, 1, modifiedSimulation) // For reactivity to work, this method must be used
            processed = true
          } else {
            // If this is a new simulation (created by someone else while the current user is online)
            if (index === state.simulations.length - 1 && !processed) {
              console.log(`New simulation ${modifiedSimulation.id} detected and pushed`)
              state.simulations.push(modifiedSimulation)
              processed = true
            }
          }
        })
      }
    })
    payload.removed.map(modifiedSimulation => {
      state.simulations.forEach((oldSimulation, index) => {
        if (oldSimulation.id === modifiedSimulation.id) {
          console.log(`Simulation ${oldSimulation.id} has been deleted`)
          state.simulations.splice(index, 1) // For reactivity to work, this method must be used
        }
      })
    })
    state.simulationsLoading = false
  },
  setUnsubscribeListener (state, payload) {
    state.unsubscribeListener = payload.unsubscribeListener
  },
  setSubscribedClient (state, client) {
    state.subscribedClient = client
  },
  setUserSimulations (state, payload) {
    state.userSimulations = payload.simulations
    state.userSimulationsHasMore = payload.hasMore || false
    state.userSimulationsLastVisible = payload.lastVisible || null
    state.userSimulationsLoading = false
  },
  appendUserSimulations (state, payload) {
    state.userSimulations = [...state.userSimulations, ...payload.simulations]
    state.userSimulationsHasMore = payload.hasMore || false
    state.userSimulationsLastVisible = payload.lastVisible || null
    state.userSimulationsLoading = false
  },
  setUserSimulationsLoading (state, loading) {
    state.userSimulationsLoading = loading
  },
  setAccessibleRecentSimulations (state, payload) {
    state.accessibleRecentSimulations = payload.simulations
    state.accessibleRecentSimulationsHasMore = payload.hasMore || false
    state.accessibleRecentSimulationsLastVisible = payload.lastVisible || null
    state.accessibleRecentSimulationsLoading = false
  },
  appendAccessibleRecentSimulations (state, payload) {
    state.accessibleRecentSimulations = [...state.accessibleRecentSimulations, ...payload.simulations]
    state.accessibleRecentSimulationsHasMore = payload.hasMore || false
    state.accessibleRecentSimulationsLastVisible = payload.lastVisible || null
    state.accessibleRecentSimulationsLoading = false
  },
  setAccessibleRecentSimulationsLoading (state, loading) {
    state.accessibleRecentSimulationsLoading = loading
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
